import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {TranslateService} from "@ngx-translate/core";
import {ConfigOverviewModel} from "../../../shared/components/models/config-overview.model";
import {ConfigCardModel} from "../../../shared/models/config-card.model";
import {ConfigTableDisplayedColumns} from "../../../shared/components/models/config-table.model";
import {Router} from "@angular/router";
import {ProgressService} from "../../../shared/services/progress.service";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {ConfigRouteEnum} from '../../../shared/enums/config-route.enum';
import {AppSettingsDataService} from "../../services/app-settings-data.service";
import {AppSettingsOverview} from "../models/app-settings.model";
import {catchError, finalize, of, tap} from "rxjs";
import {
    ConfigAmountBadgesComponent
} from "../../../shared/components/config-amount-badges/config-amount-badges.component";
import {ConfigButton} from "../../../shared/components/models/config-button";
import {ConfigWriteUserService} from "../../../shared/services/config-write-user.service";
import {RefreshService} from "../../../shared/services/refresh.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ConfigOverviewComponent} from "../../../shared/components/config-overview/config-overview.component";

@Component({
    selector: 'app-app-settings-overview',
    imports: [
        ConfigOverviewComponent,
    ],
    templateUrl: './app-settings-overview.html',
    styleUrl: './app-settings-overview.scss',
})
export class AppSettingsOverviewComponent implements OnInit {
    appSettingsOverviewModel?: ConfigOverviewModel;

    private readonly _router: Router = inject(Router);

    private allCards: ConfigCardModel[] = [];
    private displayedColumns: any[] = [];

    private products: AppSettingsOverview[] = [];

    private readonly appSettingsService = inject(AppSettingsDataService);
    private readonly progress = inject(ProgressService);
    private readonly snackBar = inject(SnackbarService)
    protected readonly ConfigRouteEnum = ConfigRouteEnum;
    private readonly translate = inject(TranslateService);
    private readonly configWriteUser = inject(ConfigWriteUserService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly refreshService = inject(RefreshService);

    ngOnInit(): void {
        this.loadAppSettings();

        this.refreshService.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.loadAppSettings();
        });
    }

    private loadAppSettings(): void {
        this.progress.startLoading();

        this.appSettingsService
            .findApplicationSettingsCount()
            .pipe(
                tap((products) => {
                    this.products = products
                        .filter(p => !p.applicationName?.endsWith('Lang'))
                        .map(p => ({
                            applicationName: p.applicationName,
                            amount: p.settingsCount
                        }));
                    this.buildOverview();
                }),
                catchError(() => {
                    this.snackBar.Error(this.translate.instant("snackbar.appSettings.error"));
                    this.products = [];
                    this.buildOverview();
                    return of([]);
                }),
                finalize(() => this.progress.stopLoading())
            )
            .subscribe();
    }

    private buildOverview(): void {
        this.allCards = this.products.map(
            (t, id) =>
                new ConfigCardModel({
                    id: id,
                    title: t.applicationName,
                    searchName: t.applicationName,
                    body: null,
                    logoUrl: null,
                    route: {
                        commands: [ConfigRouteEnum.APP_SETTINGS, t.applicationName],
                    },
                    badges: [
                        {amount: (t.amount ?? 0).toString(), label: 'appSettings.title', routerLink: undefined}
                    ],
                })
        );

        this.allCards.forEach(card => {
            (card as any).ConfigBadgesComponent = {badges: card.badges, direction: 'row'};
        });

        this.displayedColumns = ConfigTableDisplayedColumns.build(
            {HeaderCellName: 'appSettings.application', PropertyName: 'title'},
            {HeaderCellName: '', PropertyName: 'ConfigBadgesComponent', Component: ConfigAmountBadgesComponent},
        );

        this.appSettingsOverviewModel = {
            title: 'appSettings.title',
            buttons: this.configWriteUser.isAuthorized ? ConfigButton.build({
                text: 'appSettings.create',
                icon: 'pi pi-plus',
                onClick: async () => await this._router.navigate(['/', ConfigRouteEnum.APP_SETTINGS, 'new'])
            }) : [],
            cards: this.allCards,
            table: {
                settings: {
                    header: true,
                    headerAction: true,
                    search: true,
                },
                data: this.allCards,
                displayedColumns: this.displayedColumns,
                onRowClick: (row: any) => this._router.navigate(row.route.commands, row.route.extras),
            },
        };
    }
}
